import { kMatterBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { ApiError } from '@/util/errors/api-error'

/**
 * Delete (Reject) Matter Invite
 *
 * Rejects the current user's pending invitation to a matter. The invite is resolved by the
 * server from the authenticated user + the matter ID in the path, so no request body is sent.
 * Mirrors the accept endpoint but as a DELETE that returns 204 No Content (empty body).
 * @param matterId - UUID of the matter whose invite is being rejected.
 * @throws ApiError if the request fails.
 */
export async function deleteRejectMatterInvite(matterId: string): Promise<void> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'DELETE',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kMatterBasePath}${matterId}/members/me`
    const response = await fetch(apiUrl, requestOptions)

    if (!response.ok) {
      // Only a failure response carries a body; parse defensively for the error detail.
      const data = await response.json().catch(() => ({}))

      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
            matterId,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`, data.detail)
    }

    // Success is a 204 No Content with an empty body, so there is nothing to parse or return.
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, matterId },
        })
      })
    }
    throw error
  }
}
